import { useRef, useState } from 'react';
import type { FormLanguage, TemplateCategory } from '@chat-emp/shared';

import { Icon } from '../components/Icon.js';
import {
  CATEGORY_LABEL,
  matchesTemplate,
  TEMPLATE_CATEGORIES,
  templatesIn,
} from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { plural } from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import styles from './FormTemplateDialog.module.css';

type Shelf = TemplateCategory | 'all';

/** Every new form opens here: the "+" going straight to an empty builder made the
 * employer rank a blank page against forms they had not seen. A GALLERY, not a
 * list-and-preview: a card IS the press, and nothing is written by it. Fifty
 * templates need a rail and a search to stay a gallery rather than a wall. */
export function FormTemplateDialog({
  templates,
  loading = false,
  error = false,
  language,
  onLanguage,
  onClose,
  onBlank,
  onUse,
}: {
  templates: Form[];
  /** The catalogue is still on its way, or did not arrive: neither is "not
   *  yet available in this language", and neither has anything to offer. */
  loading?: boolean;
  error?: boolean;
  language: FormLanguage;
  onLanguage: (language: FormLanguage) => void;
  onClose: () => void;
  onBlank: () => void;
  onUse: (template: Form) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, dialogRef);
  const [query, setQuery] = useState('');
  const [shelf, setShelf] = useState<Shelf>('all');

  const inLanguage = templatesIn(templates, language);
  const searching = query.trim() !== '';
  const sections = TEMPLATE_CATEGORIES.filter(
    (category) => shelf === 'all' || category === shelf,
  ).map((category) => ({
    category,
    items: inLanguage.filter(
      (template) => template.category === category && matchesTemplate(template, query),
    ),
    translated: templatesIn(templates, language, category).length,
    authored: templatesIn(templates, 'en', category).length,
  }));
  const visible = searching ? sections.filter((section) => section.items.length > 0) : sections;

  return (
    <div className={styles.overlay}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a template"
        ref={dialogRef}
      >
        <div className={styles.header}>
          <span className={styles.iconBadge}>
            <Icon name="listChecks" size={18} />
          </span>
          <div className={styles.heading}>
            <h2 className={styles.title}>Choose a template</h2>
            <p className={styles.subtitle}>Opens as a new form you can edit.</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className={styles.toolbar}>
          <label className={styles.search}>
            <Icon name="search" size={16} />
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search templates"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Escape in a search field clears it; only an empty one lets
                // the press through to close the gallery.
                if (event.key === 'Escape' && query !== '') {
                  event.stopPropagation();
                  setQuery('');
                }
              }}
            />
          </label>
          <LanguageSwitch value={language} onChange={onLanguage} />
        </div>

        <div className={styles.body}>
          <nav className={styles.rail} aria-label="Template groups">
            <button
              type="button"
              className={shelf === 'all' ? styles.railRowOn : styles.railRow}
              aria-pressed={shelf === 'all'}
              onClick={() => setShelf('all')}
            >
              <span className={styles.railName}>All templates</span>
              <span className={styles.railCount}>{inLanguage.length}</span>
            </button>
            {TEMPLATE_CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                className={shelf === category ? styles.railRowOn : styles.railRow}
                aria-pressed={shelf === category}
                onClick={() => setShelf(category)}
              >
                <span className={styles.railName}>{CATEGORY_LABEL[category].name}</span>
                <span className={styles.railCount}>
                  {templatesIn(templates, language, category).length}
                </span>
              </button>
            ))}
          </nav>

          <div className={styles.main}>
            {visible.length === 0 && (
              <p className={styles.empty}>No templates match “{query.trim()}”.</p>
            )}
            {visible.map((section, index) => (
              <section key={section.category} className={styles.section}>
                <div className={styles.sectionHead}>
                  <h3 className={styles.sectionTitle}>{CATEGORY_LABEL[section.category].name}</h3>
                  <span className={styles.sectionTagline}>
                    {CATEGORY_LABEL[section.category].tagline}
                  </span>
                  <span className={styles.sectionCount}>
                    {plural(section.items.length, 'template')}
                  </span>
                </div>
                {/* The blank card belongs to the first group whatever that group
                    holds: it is the door to the builder, and an untranslated
                    or still-loading catalogue must not close it. */}
                {(index === 0 && !searching) || section.items.length > 0 ? (
                  <ul className={styles.grid}>
                    {index === 0 && !searching && (
                      <li>
                        <button
                          type="button"
                          className={styles.card}
                          aria-label="Blank form"
                          onClick={onBlank}
                        >
                          <span className={styles.blankFace}>
                            <Icon name="plus" size={24} />
                          </span>
                          <span className={styles.text}>
                            <span className={styles.name}>Blank form</span>
                            <span className={styles.meta}>Start from scratch</span>
                          </span>
                        </button>
                      </li>
                    )}
                    {section.items.map((template) => (
                      <li key={template.id}>
                        <button
                          type="button"
                          className={styles.card}
                          onClick={() => onUse(template)}
                        >
                          <span className={styles.face}>
                            {template.fields.map((field, position) => (
                              <span key={field.id} className={styles.line}>
                                <span className={styles.lineNumber}>{position + 1}</span>
                                <span className={styles.lineText}>{field.label}</span>
                              </span>
                            ))}
                          </span>
                          <span className={styles.text}>
                            <span className={styles.name}>{template.name}</span>
                            <span className={styles.meta}>
                              {plural(template.fields.length, 'question')}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {/* An untranslated group says so rather than showing the English:
                    the card is the press, and a press here would send words the
                    switch was set to avoid. */}
                {section.translated === 0 && !searching && (loading || error) && index === 0 && (
                  <p className={styles.empty}>
                    {error ? 'Templates could not be loaded.' : 'Loading templates…'}
                  </p>
                )}
                {section.translated === 0 && !searching && !loading && !error && (
                  <p className={styles.empty}>
                    <span>Not yet available in this language.</span>
                    {language !== 'en' && section.authored > 0 && (
                      <button
                        type="button"
                        className={styles.emptyAction}
                        onClick={() => onLanguage('en')}
                      >
                        Show the {section.authored} in English
                      </button>
                    )}
                  </p>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
